/**
 * This implementation repackages a minimal API based on a heavily outdated version of react-toast.  We plan to swap/update the internal implementation after the app is fully using the `eds.useToast` solution.
 * https://github.com/fkhadra/react-toastify/tree/v4.5.2
 */
import React from 'react';
import {
  Slide,
  ToastContent,
  ToastPosition,
  toast as vendorToast,
} from 'react-toastify';

import { SharedToastProps } from '../../types/SharedToastProps';
import { wordCount } from '../../utils/wordCount';
import { Toast } from './Toast';

const DURATION_PER_WORD_MS = 500;
const MIN_DURATION_MS = 6000;

const getAutoDismissDuration = (content: string) => {
  return Math.max(wordCount(content) * DURATION_PER_WORD_MS, MIN_DURATION_MS);
};

interface VendorToastProps {
  closeToast: () => void;
}

// https://github.com/fkhadra/react-toastify/tree/v4.5.2#toastcontainer
const DEFAULT_OPTIONS = {
  closeButton: false,
  closeOnClick: false,
  draggable: false,
  hideProgressBar: true,
  className: 'eds-react-toastify_toast-container',
  position: 'bottom-center' as ToastPosition,
  transition: Slide,
};

export const showToast = (props: SharedToastProps) => {
  const { key: toastKey, ...toastProps } = props;
  const autoCloseIsFalse = toastProps.autoDismiss === false;
  const textContent = toastProps.message + '\n' + (toastProps.title ?? '');

  const enableDismiss =
    toastProps.action || toastProps.onDismiss || autoCloseIsFalse;

  const renderToast = ({ closeToast }: VendorToastProps) => {
    const onDismiss = enableDismiss
      ? () => {
          toastProps.onDismiss?.();
          closeToast();
        }
      : undefined;

    const action = toastProps.action
      ? {
          ...toastProps.action,
          onClick: () => {
            toastProps.action?.onClick?.();
            closeToast();
          },
        }
      : undefined;

    return <Toast {...toastProps} action={action} onDismiss={onDismiss} />;
  };

  return vendorToast(renderToast as unknown as ToastContent, {
    ...DEFAULT_OPTIONS,
    autoClose:
      toastProps.action || autoCloseIsFalse
        ? false
        : getAutoDismissDuration(textContent),
    toastId: toastKey,
  });
};
